import { useEffect, useState } from 'react';
import { Modal, Button, Tag, Image, Tabs, Spin } from 'antd';
import { Icon } from 'Components';
import { TemplateDetails } from 'Pages/PhishingManager/CampaignSetup';

interface PhishingModalProps {
    loading: boolean;
    visible: boolean;
    onClose: () => void;
    onNext: () => void;
    onLanguageChange: (language: number) => void;
    templateDetails: TemplateDetails | null;
}

const PhishingModal = ({ loading, visible, onClose, onNext, onLanguageChange, templateDetails }: PhishingModalProps) => {
    const [activeTab, setActiveTab] = useState('email');
    const { TabPane } = Tabs;

    const handleTemplateChange = (lang: { id: number, name?: string | undefined }) => {
        onLanguageChange(lang.id);
    }

    const openInNewTab = (htmlContent: string) => {
        if (!htmlContent) return;
        const newWindow = window.open();
        newWindow?.document.write(htmlContent);
        newWindow?.document.close();
    };

    const injectIframeEmailLogic = (html: string | undefined): string => {
        if (!html) return "";

        const logicScript = `
            <style>
                a { pointer-events: none !important; cursor: default !important; }
                html, body { cursor: pointer; }
            </style>
            <script>
                document.addEventListener('click', () => {
                    window.parent.postMessage('IFRAME_EMAIL_CLICKED', '*');
                });
            </script>
        `;

        if (html.includes("</head>")) {
            return html.replace("</head>", `${logicScript}</head>`);
        }
        if (html.includes("<body>")) {
            return html.replace("<body>", `<head>${logicScript}</head><body>`);
        }
        return `<head>${logicScript}</head>` + html;
    };

    const injectIframeLandingLogic = (html: string | undefined): string => {
        if (!html) return "";

        const logicScript = `
            <style>
                a { pointer-events: none !important; cursor: default !important; }
                html, body { cursor: pointer; }
            </style>
            <script>
                document.addEventListener('click', () => {
                    window.parent.postMessage('IFRAME_LANDING_CLICKED', '*');
                });
            </script>
        `;

        if (html.includes("</head>")) {
            return html.replace("</head>", `${logicScript}</head>`);
        }
        if (html.includes("<body>")) {
            return html.replace("<body>", `<head>${logicScript}</head><body>`);
        }
        return `<head>${logicScript}</head>` + html;
    };

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (event.data === 'IFRAME_EMAIL_CLICKED') {
                openInNewTab(templateDetails?.email_template_html ?? "");
            }
            if (event.data === 'IFRAME_LANDING_CLICKED') {
                openInNewTab(templateDetails?.landing_page_html ?? "");
            }
        };

        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, [templateDetails]);

    return (
        <Modal
            closable={false}
            className="phishing-preview-modal"
            visible={visible}
            onCancel={onClose}
            footer={
                <div className="modal-footer-custom">
                    <div className="footer-buttons">
                        <Button
                            type="text"
                            size="large"
                            className="btn-cancel"
                            onClick={onClose}
                        >
                            Close
                        </Button>
                        <Button
                            type="primary"
                            size="large"
                            className="btn-next"
                            onClick={onNext}
                        >
                            Next
                            <span style={{ marginLeft: 8, display: 'inline-flex' }}>
                                <svg
                                    width="16"
                                    height="12"
                                    viewBox="0 0 16 12"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M1 6H14.3333M14.3333 6L9.33333 1M14.3333 6L9.33333 11"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                            </span>
                        </Button>
                    </div>
                </div>
            }
            centered
            width={495}
            bodyStyle={{ padding: 0 }}
            style={{
                borderBottom: '1px solid #EAEAEB'
            }}
        >
            <Spin spinning={loading}>
                <div className="pm-modal-header">
                    <div className="pm-modal-header-left">
                        <span className="pm-modal-title">{templateDetails?.name || "Template Name"}</span>
                    </div>
                    <button
                        className="pm-modal-close-icon"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <Icon type="campaignModalClose" />
                    </button>
                </div>

                <div className="modal-section description-section">
                    <div className="section-image">
                        <Image
                            src={templateDetails?.image_path || "./placeholderImage.png"}
                            alt="Phishing simulation preview"
                            preview={false}
                            style={{ borderRadius: '6px', width: '100%' }}
                        />
                        <Tag color="green" className="badge-beginner">
                            {templateDetails?.difficulty}
                        </Tag>
                    </div>

                    <div className="section-description">
                        <h3 className="description-title">Description</h3>
                        <p className="description-text">
                            {templateDetails?.description || "No description available for this template."}
                        </p>
                    </div>
                </div>

                <div className="modal-section tabs-section">
                    <Tabs
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        className="phishing-tabs"
                    ><TabPane tab="Email" key="email">
                            <div className="tab-content">
                                <div style={{ position: "relative" }}>
                                    <iframe
                                        title='Email Template Preview'
                                        style={{
                                            width: "100%",
                                            height: "296px",
                                            border: "none",
                                            display: "block",
                                            overflow: "auto"
                                        }}
                                        srcDoc={injectIframeEmailLogic(templateDetails?.email_template_html) || ""}
                                    />
                                </div>
                            </div>
                        </TabPane>

                        <TabPane tab="Landing Page" key="landing_page">
                            <div className="tab-content">
                                <div style={{ position: "relative" }}>
                                    <iframe
                                        title='Landing Page Template Preview'
                                        style={{
                                            width: "100%",
                                            height: "296px",
                                            border: "none",
                                            display: "block",
                                            overflow: "auto"
                                        }}
                                        srcDoc={injectIframeLandingLogic(templateDetails?.landing_page_html) || ""}
                                    />
                                </div>
                            </div>
                        </TabPane>

                    </Tabs>
                </div>

                <div className="modal-section language-section">
                    <label className="language-label">
                        Select Language <span className="required">*</span>
                    </label>
                    <div className="language-tags">
                        {templateDetails?.languages?.map((lang: { id: number, name?: string | undefined }) => (
                            <Tag
                                key={lang.id}
                                onClick={() => handleTemplateChange(lang)}
                                className={`language-tag ${templateDetails?.selected_packaged_template_language_id === lang.id ? 'selected' : ''}`}
                                style={{
                                    backgroundColor: templateDetails?.selected_packaged_template_language_id === lang.id ? '#B8E1F6' : '#EAEAEB',
                                }}
                            >
                                {lang.name}
                            </Tag>
                        ))}
                    </div>
                </div>
            </Spin>
        </Modal>
    );
};

export default PhishingModal;